import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { Employee } from "../../types/EmployeeTypes";
import api from "../../utils/api";

interface EmployeeState {
  allEmployees: Employee[];
  activeEmployees: Employee[];
  loading: boolean;
  error: string | null;
}

const initialState: EmployeeState = {
  allEmployees: [],
  activeEmployees: [],
  loading: false,
  error: null,
};

// GET ALL EMPLOYEES
export const fetchAllEmployees = createAsyncThunk(
  "employees/fetchall",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/allocation-tool/employee/get-all`
      );
      return res.data.data as Employee[];
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch all employees"
        );
      }
      return rejectWithValue("Failed to fetch all employees");
    }
  }
);

// GET ACTIVE EMPLOYEES
export const fetchActiveEmployees = createAsyncThunk(
  "employees/fetchActive",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/allocation-tool/employee/get-active`
      );
      return res.data.data as Employee[];
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch active employees"
        );
      }
      return rejectWithValue("Failed to fetch active employees");
    }
  }
);

// UPDATE EMPLOYEE
export const updateEmployee = createAsyncThunk(
  "employees/update",
  async (
    payload: {
      emp_id: number;
      email?: string;
      emp_name?: string;
      role?: string;
      verified?: boolean;
      shift: string;
      efficiency?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      await api.post(
        `/allocation-tool/employee/update-details`,
        payload
      );
      return payload; // return so we can update UI
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to update employees"
        );
      }
      return rejectWithValue("Failed to update employees");
    }
  }
);

// UPDATE ACTIVE
export const updateActiveEmployee = createAsyncThunk(
  "employees/updateActive",
  async (
    payload: { emp_name: string; action: "present" | "absent" },
    { rejectWithValue }
  ) => {
    try {
      await api.post(
        `/allocation-tool/employee/update-active`,
        payload
      );
      return payload;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to update active status"
        );
      }
      return rejectWithValue("Failed to update active status");
    }
  }
);

const employeeSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH VERIFIED EMPLOYEES
      .addCase(fetchAllEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllEmployees.fulfilled,
        (state, action: PayloadAction<Employee[]>) => {
          state.loading = false;
          state.allEmployees = action.payload;
        }
      )
      .addCase(fetchAllEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // FETCH ACTIVE EMPLOYEES
      .addCase(fetchActiveEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchActiveEmployees.fulfilled,
        (state, action: PayloadAction<Employee[]>) => {
          state.loading = false;
          state.activeEmployees = action.payload;
        }
      )
      .addCase(fetchActiveEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // UPDATE EMPLOYEE
      .addCase(updateEmployee.fulfilled, (state, action) => {
        const updated = action.payload;

        // update verified list
        state.allEmployees = state.allEmployees.map((emp) =>
          emp.emp_id === updated.emp_id ? { ...emp, ...updated } : emp
        );

        // update active list
        state.activeEmployees = state.activeEmployees.map((emp) =>
          emp.emp_id === updated.emp_id ? { ...emp, ...updated } : emp
        );
      })

      .addCase(updateActiveEmployee.fulfilled, (state, action) => {
        const { emp_name, action: status } = action.payload;

        // Update in allEmployees
        state.allEmployees = state.allEmployees.map((emp) => {
          if (emp.emp_name !== emp_name) return emp;

          if (status === "present") {
            return { ...emp, active: true };
          }

          if (status === "absent") {
            return { ...emp, active: false };
          }
          return { ...emp, active: false };
        });

        // Update activeEmployees
        if (status === "present") {
          // add employee to active list if not already
          const emp = state.allEmployees.find((e) => e.emp_name === emp_name);
          if (emp && !state.activeEmployees.some((e) => e.emp_name === emp_name)) {
            state.activeEmployees.push(emp);
          }
        } else {
          // remove from active list
          state.activeEmployees = state.activeEmployees.filter(
            (e) => e.emp_name !== emp_name
          );
        }
      });
  },
});

export default employeeSlice.reducer;
